if (!customElements.get('sw-ticker')) {
  class SwTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sw-ticker-track]');
      if (!this.track) return;
      this.onResize = this.onResize.bind(this);
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.toggleAttribute('data-sw-paused', !entries.some((entry) => entry.isIntersecting));
      });
      this.visibilityObserver.observe(this);

      /* Start with the server-rendered items, or the session's cached sample. A fresh sample is
         fetched once the page is idle and saved for the next page view, so the moving bar never
         swaps its contents mid-scroll. */
      const cached = this.readCache();
      if (cached) this.track.innerHTML = cached;
      this.build();
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
      if (!cached) this.whenIdle(() => this.refreshSample());
    }

    cacheKey() {
      return `sw-ticker:${this.dataset.collectionUrl}:${this.dataset.productCount}:${this.dataset.limit}`;
    }

    readCache() {
      try {
        return window.sessionStorage.getItem(this.cacheKey());
      } catch (error) {
        return null;
      }
    }

    writeCache(html) {
      try {
        window.sessionStorage.setItem(this.cacheKey(), html);
      } catch (error) {
        /* Private mode or a full quota: the sample just is not reused. */
      }
    }

    whenIdle(callback) {
      const run = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 4000 });
        else window.setTimeout(callback, 1500);
      };
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    }

    async refreshSample() {
      const items = await this.loadRandom();
      if (!items || !items.length || !this.isConnected) return;
      const holder = document.createElement('ul');
      holder.append(...items);
      this.writeCache(holder.innerHTML);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
    }

    onResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        const half = this.track.scrollWidth / 2;
        if (this.clientWidth > half || Math.abs(half - this.halfWidth) > 8) this.build();
      });
    }

    /* The loop is two identical halves, so a -50% shift lands back on the start. */
    build() {
      const source = this.source || Array.from(this.track.children);
      if (!source.length) return;
      this.source = source.map((item) => item.cloneNode(true));

      const items = this.source.map((item) => item.cloneNode(true));
      this.track.replaceChildren(...items);

      const viewport = this.clientWidth || window.innerWidth;
      let guard = 0;
      while (this.track.scrollWidth < viewport * 1.5 && guard < 12) {
        this.track.append(...this.source.map((item) => item.cloneNode(true)));
        guard += 1;
      }

      this.halfWidth = this.track.scrollWidth;
      const tail = Array.from(this.track.children).map((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        return clone;
      });
      this.track.append(...tail);

      const speed = Number(this.dataset.speed) || 55;
      this.style.setProperty('--sw-ticker-duration', `${Math.round(this.halfWidth / speed)}s`);
      this.setAttribute('data-sw-ready', '');
    }

    async loadRandom() {
      const collectionUrl = this.dataset.collectionUrl;
      const count = Number(this.dataset.productCount) || 0;
      const limit = Number(this.dataset.limit) || 20;
      if (!collectionUrl || count < 1) return [];

      const pageSize = 50;
      const pages = Math.max(1, Math.ceil(count / pageSize));
      /* The catalog is alphabetical by brand, so sample one page per band to
         keep a single brand from filling the whole bar. */
      const bands = Math.min(pages, 3);
      const numbers = [];
      for (let band = 0; band < bands; band += 1) {
        const start = Math.floor((band * pages) / bands) + 1;
        const end = Math.floor(((band + 1) * pages) / bands);
        const span = Math.max(1, end - start + 1);
        const page = Math.min(pages, start + Math.floor(Math.random() * span));
        if (!numbers.includes(page)) numbers.push(page);
      }

      const groups = await Promise.all(
        numbers.map(async (page) => {
          try {
            return await this.fetchPage(collectionUrl, page);
          } catch (error) {
            return [];
          }
        })
      );
      return this.shuffle(groups.flat()).slice(0, limit);
    }

    async fetchPage(collectionUrl, page) {
      const url = new URL(collectionUrl, window.location.origin);
      url.searchParams.set('view', 'sw-ticker');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('ticker page');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll('.sw-ticker__item'));
    }

    shuffle(items) {
      const copy = items.slice();
      for (let index = copy.length - 1; index > 0; index -= 1) {
        const swap = Math.floor(Math.random() * (index + 1));
        const current = copy[index];
        copy[index] = copy[swap];
        copy[swap] = current;
      }
      return copy;
    }
  }

  customElements.define('sw-ticker', SwTicker);
}
